import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Button, Menu, MenuItem, Tab, Tabs } from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { useAuth } from './AuthGate';
import useIsMobile from '../hooks/useIsMobile';

const EXCLUDED_PATHS = ['/', '/exec/filtered-brief', '/exec/deck'];

export const SECTIONS = {
  participants: {
    matchPrefix: '/participants',
    pages: [
      { label: 'All Entrants', href: '/participants/all' },
      { label: "Today's Entrants", href: '/participants/today' },
      { label: 'Participant Data', href: '/participants/briefing', permission: 'exec' },
      { label: 'Tag Analytics', href: '/participants/tags' },
    ],
  },
  dev: {
    matchExact: ['/exec', '/impact', '/platform-usage', '/events/xperts24', '/events/xperts25'],
    pages: [
      { label: 'Exec Roll Up', href: '/exec' },
      { label: 'Impact', href: '/impact' },
      { label: 'Platform Usage', href: '/platform-usage' },
      { label: 'Xperts 2024', href: '/events/xperts24' },
      { label: 'Xperts 2025', href: '/events/xperts25' },
    ],
  },
  admin: {
    matchPrefix: '/admin',
    pages: [
      { label: 'Tag Admin', href: '/admin/tags', permission: 'admin.tags' },
      { label: 'Content Admin', href: '/admin/content', permission: 'admin.content' },
      { label: 'Container Images', href: '/admin/images', permission: 'admin.images' },
      { label: 'API Tools', href: '/admin/api-tools', permission: 'admin.api_tools' },
      { label: 'Access Control', href: '/admin/access', permission: 'admin.access' },
      { label: 'Bot Detection', href: '/admin/bot-detection', permission: 'admin.access' },
      { label: 'Internal Domains', href: '/admin/internal-domains', permission: 'admin.access' },
      { label: 'Influence Cutover', href: '/admin/influence-cutover', permission: 'admin.access' },
      { label: 'Domain Matches', href: '/admin/domain-matches', permission: 'admin.access' },
      { label: 'Notifications', href: '/admin/notifications', permission: 'admin.access' },
      { label: 'Communications', href: '/admin/communications', permission: 'admin.access' },
      { label: 'Salesforce Login', href: '/admin/salesforce-auth', permission: 'admin.access' },
      { label: 'Exclusions', href: '/admin/exclusions', permission: 'admin.access' },
      { label: 'Agent Impl', href: '/admin/agent-impl', permission: 'admin.access' },
      { label: 'Feature Requests', href: '/admin/feature-requests', permission: 'admin.access' },
      { label: 'Chat Activity', href: '/admin/chat-activity', permission: 'admin.access' },
      { label: 'Audit Log', href: '/admin/audit', permission: 'admin.access' },
      { label: 'Email Archive', href: '/admin/email-archive', permission: 'admin.access' },
      { label: 'Resend Report', href: '/admin/resend-report', permission: 'admin.access' },
      { label: 'Email Retry', href: '/admin/email-retry', permission: 'admin.access' },
      { label: 'Inbound Email', href: '/admin/inbound-email', permission: 'admin.access' },
    ],
  },
  help: {
    matchExact: ['/architecture', '/howto', '/methodology', '/page-guidelines', '/whats-new', '/about'],
    pages: [
      { label: 'Architecture', href: '/architecture' },
      { label: 'How To', href: '/howto' },
      { label: 'Methodology', href: '/methodology', permission: 'exec' },
      { label: 'Page Guidelines', href: '/page-guidelines' },
      { label: "What's New", href: '/whats-new' },
      { label: 'About', href: '/about' },
    ],
  },
  events: {
    matchPrefix: '/events',
    pages: [
      { label: 'All Events', href: '/events' },
      { label: 'New Event', href: '/events/new' },
      { label: 'Audience Lab', href: '/events/audience-lab', permission: 'events' },
      { label: 'Templates', href: '/events/templates', permission: 'events' },
      { label: 'Settings', href: '/events/settings', permission: 'admin.access' },
      { label: 'Block List', href: '/events/blacklist', permission: 'admin.access' },
      { label: 'Registration Health', href: '/events/registration-health', permission: 'admin.access' },
    ],
  },
};

// The admin pages are shown in the same three groups as the side menu (Layout.jsx), as dropdowns, so the bar never scrolls sideways.
// Every admin page must be in exactly one group (SectionSubNav.test.js guards that).
SECTIONS.admin.groups = [
  { label: 'Backend', hrefs: ['/admin/images', '/admin/api-tools', '/admin/access', '/admin/bot-detection', '/admin/internal-domains',
    '/admin/influence-cutover', '/admin/salesforce-auth', '/admin/agent-impl', '/admin/feature-requests', '/admin/audit'] },
  { label: 'Comms', hrefs: ['/admin/notifications', '/admin/communications', '/admin/chat-activity', '/admin/email-archive',
    '/admin/resend-report', '/admin/email-retry', '/admin/inbound-email'] },
  { label: 'Content', hrefs: ['/admin/tags', '/admin/content', '/admin/domain-matches', '/admin/exclusions'] },
];

function detectSection(pathname) {
  if (EXCLUDED_PATHS.includes(pathname)) return null;
  for (const [key, section] of Object.entries(SECTIONS)) {
    if (section.matchExact && section.matchExact.includes(pathname)) return key;
  }
  for (const [key, section] of Object.entries(SECTIONS)) {
    if (section.matchPrefix && pathname.startsWith(section.matchPrefix)) return key;
  }
  return null;
}

function getActiveTab(pages, pathname) {
  const exact = pages.find(p => p.href === pathname);
  if (exact) return exact.href;
  // For sub-pages like /events/123/registrations, match the closest prefix
  const byLength = [...pages].sort((a, b) => b.href.length - a.href.length);
  const prefix = byLength.find(p => pathname.startsWith(p.href + '/'));
  return prefix ? prefix.href : false;
}

const NAV_BUTTON_SX = {
  minHeight: 36, fontSize: '0.78rem', fontWeight: 500, textTransform: 'none', px: '14px', py: '6px', borderRadius: 0,
  color: 'rgba(255,255,255,0.6)', borderBottom: '3px solid transparent',
  '&:hover': { color: 'rgba(255,255,255,0.9)', bgcolor: 'rgba(255,255,255,0.05)' },
  '&.active': { color: '#fff', borderBottomColor: 'rgb(218,41,28)' },
};

function GroupMenu({ group, pages, activeHref, navigate }) {
  const [anchor, setAnchor] = useState(null);
  const active = pages.some(p => p.href === activeHref);
  const id = `admin-group-${group.label.toLowerCase()}`;
  return (
    <>
      <Button
        className={active ? 'active' : undefined}
        sx={NAV_BUTTON_SX}
        aria-haspopup="menu"
        aria-expanded={anchor ? 'true' : undefined}
        aria-controls={anchor ? `${id}-menu` : undefined}
        id={id}
        data-testid={id}
        onClick={(e) => setAnchor(e.currentTarget)}
        endIcon={<ArrowDropDownIcon fontSize="small" />}
      >
        {group.label}
      </Button>
      <Menu
        id={`${id}-menu`}
        anchorEl={anchor}
        open={!!anchor}
        onClose={() => setAnchor(null)}
        MenuListProps={{ 'aria-labelledby': id, dense: true }}
        PaperProps={{ sx: { bgcolor: '#231818', color: '#fff', border: '1px solid rgba(255,255,255,0.15)' } }}
      >
        {pages.map(page => (
          <MenuItem
            key={page.href}
            component="a"
            href={page.href}
            selected={page.href === activeHref}
            sx={{ fontSize: '0.82rem', '&.Mui-selected': { bgcolor: 'rgba(218,41,28,0.25)' } }}
            onClick={(e) => {
              setAnchor(null);
              if (e.button !== 0 || e.ctrlKey || e.metaKey || e.shiftKey) return;
              e.preventDefault();
              navigate(page.href);
            }}
          >
            {page.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

export default function SectionSubNav() {
  const location = useLocation();
  const navigate = useNavigate();
  const { canRead } = useAuth();
  const isMobile = useIsMobile();

  if (isMobile) return null;

  const sectionKey = detectSection(location.pathname);
  if (!sectionKey) return null;

  const section = SECTIONS[sectionKey];
  const pages = section.pages.filter(p => !p.permission || canRead(p.permission));
  if (pages.length <= 1) return null;

  const activeTab = getActiveTab(pages, location.pathname);

  if (section.groups) {
    const byHref = new Map(pages.map(p => [p.href, p]));
    const groups = section.groups
      .map(g => ({ group: g, pages: g.hrefs.map(h => byHref.get(h)).filter(Boolean) }))
      .filter(g => g.pages.length > 0);
    return (
      <Box
        component="nav"
        aria-label="Section navigation"
        sx={{ bgcolor: '#231818', borderBottom: '1px solid rgba(255,255,255,0.1)', display: 'flex', flexWrap: 'wrap', minHeight: 36 }}
      >
        {groups.map(g => (
          <GroupMenu key={g.group.label} group={g.group} pages={g.pages} activeHref={activeTab || ''} navigate={navigate} />
        ))}
      </Box>
    );
  }

  return (
    <Box
      component="nav"
      aria-label="Section navigation"
      sx={{ bgcolor: '#231818', borderBottom: '1px solid rgba(255,255,255,0.1)' }}
    >
      <Tabs
        value={activeTab}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        TabIndicatorProps={{ style: { backgroundColor: 'rgb(218,41,28)', height: 3 } }}
        sx={{
          minHeight: 36,
          '& .MuiTab-root': {
            minHeight: 36,
            fontSize: '0.78rem',
            fontWeight: 500,
            color: 'rgba(255,255,255,0.6)',
            textTransform: 'none',
            px: '14px',
            py: '6px',
            '&.Mui-selected': { color: '#fff' },
            '&:hover': { color: 'rgba(255,255,255,0.9)', bgcolor: 'rgba(255,255,255,0.05)' },
          },
          '& .MuiTabScrollButton-root': {
            color: 'rgba(255,255,255,0.5)',
            '&.Mui-disabled': { opacity: 0.25 },
          },
        }}
      >
        {pages.map(page => (
          <Tab
            key={page.href}
            label={page.label}
            value={page.href}
            component="a"
            href={page.href}
            onClick={(e) => {
              if (e.button !== 0 || e.ctrlKey || e.metaKey || e.shiftKey) return;
              e.preventDefault();
              navigate(page.href);
            }}
          />
        ))}
      </Tabs>
    </Box>
  );
}
